import { BitkitTour, BitkitUISideNav, IconMcp, useResponsive, useUISideNav } from '@bitrise/bitkit-v2';
import { useDisclosure } from '@chakra-ui/react/hooks';
import { useEffect, useRef, useState } from 'react';

import { McpConnectionStatus } from '@/models/User';

import ConnectMcpDialog from './ConnectMcpDialog';
import { McpSignpostSource, trackMcpSetupStarted, trackMcpSignpostShown } from './mcpTracking';
import useMcpNavTour from './useMcpNavTour';

const STATUS_TEXTS: Record<McpConnectionStatus, string> = {
  none: 'Not connected',
  connected: 'Connected',
  expired: 'Connection expired',
};

// The side nav's status dot only comes in these three; an expired connection needs acting on, so it
// reads as critical rather than the amber it had of its own.
const STATUS_DOTS: Record<McpConnectionStatus, 'critical' | 'neutral' | 'success'> = {
  none: 'neutral',
  connected: 'success',
  expired: 'critical',
};

type McpNavWidgetProps = {
  status: McpConnectionStatus;
};

const McpNavWidget = ({ status }: McpNavWidgetProps) => {
  const { open, onOpen, onClose } = useDisclosure();
  const { expanded } = useUISideNav();
  const { isMobile } = useResponsive();
  const linkRef = useRef<HTMLButtonElement>(null);
  const [dialogSource, setDialogSource] = useState<McpSignpostSource>('side_nav_mcp_widget');
  const { markSetupStarted, tour } = useMcpNavTour({
    enabled: !isMobile,
    isSideNavCollapsed: !expanded,
    status,
    suppressed: open,
    targetRef: linkRef,
  });

  useEffect(() => {
    trackMcpSignpostShown({
      source: 'side_nav_mcp_widget',
      mcpConnectionStatus: status,
      isSideNavCollapsed: !expanded,
    });
  }, [status, expanded]);

  const handleOpen = () => {
    const source = tour.open ? 'side_nav_mcp_tour' : 'side_nav_mcp_widget';
    markSetupStarted();
    trackMcpSetupStarted({
      source,
      mcpConnectionStatus: status,
      isSideNavCollapsed: !expanded,
    });
    setDialogSource(source);
    onOpen();
  };

  return (
    <>
      <BitkitUISideNav.Link
        icon={IconMcp}
        onClick={handleOpen}
        ref={linkRef}
        secondaryText={STATUS_TEXTS[status]}
        status={STATUS_DOTS[status]}
      >
        Bitrise MCP
      </BitkitUISideNav.Link>
      <ConnectMcpDialog open={open} onClose={onClose} source={dialogSource} status={status} />
      <BitkitTour tour={tour} />
    </>
  );
};

export default McpNavWidget;
